import { useEffect, useState } from 'react';

// CSRep Developer API Terms v2.0 rules this card must keep:
// - §8: the official badge is visible wherever CSRep data shows and links to
//   the player's csrep.gg profile.
// - §4/§6: Reputation Signals keep their probabilistic framing. The
//   "probabilistic" tag stays next to the score, and nothing here is colored like a ban: no red,
//   no traffic-light scale on the trust score.
// - Restricted profiles render as restricted, never as an ordinary profile.

// Shown as the tag's hover text; the visible "probabilistic" tag is the
// minimum §4 framing and must stay next to the score.
const DISCLAIMER =
  "CSRep's probabilistic assessment. Informational only, not proof of cheating or innocence.";

const count = (n) => Math.round(n).toLocaleString('en-US');
const pct = (fraction) => Math.round(fraction * 100);

function formatDate(iso) {
  const t = Date.parse(iso);
  return Number.isFinite(t)
    ? new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;
}

// Commendation glyphs redrawn after the in-game scoreboard icons, in the
// game's order: friendly (smiley), teaching (mortarboard), leader (crown).
const svgProps = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'currentColor', 'aria-hidden': true };
const FriendlyIcon = () => (
  <svg {...svgProps}>
    <path fillRule="evenodd" d="M12 2a10 10 0 1 1 0 20 10 10 0 0 1 0-20zM8.5 8a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zm7 0a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zM7 13.5a5 5 0 0 0 10 0z" />
  </svg>
);
const TeachingIcon = () => (
  <svg {...svgProps}>
    <path d="M12 4 1 9l11 5 9-4.1V16h2V9L12 4z" />
    <path d="M5 12.6V16c0 1.7 3.1 3 7 3s7-1.3 7-3v-3.4l-7 3.2-7-3.2z" />
  </svg>
);
const LeaderIcon = () => (
  <svg {...svgProps}>
    <path d="M2 7l5 4 5-7 5 7 5-4-2 11H4L2 7z" />
    <path d="M4 19.5h16V21H4z" />
  </svg>
);
const COMMENDATIONS = [['friendly', FriendlyIcon], ['teaching', TeachingIcon], ['leader', LeaderIcon]];

// status: 'loading' | 'ready' | 'unavailable'
export function useCsrep(steamId) {
  const [state, setState] = useState({ status: 'loading', data: null });

  useEffect(() => {
    if (!/^\d{17}$/.test(steamId || '')) {
      setState({ status: 'unavailable', data: null });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading', data: null });
    fetch(`/api/csrep/${steamId}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled) return;
        setState(data && data.available
          ? { status: 'ready', data }
          : { status: 'unavailable', data: null, reason: data && data.reason });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'unavailable', data: null });
      });
    return () => {
      cancelled = true;
    };
  }, [steamId]);

  return state;
}

function Footer({ data }) {
  return (
    <div className="pp-leetify-footer">
      <span className="pp-csrep-links">
        <a className="pp-link pp-leetify-link" href={data.profileUrl} target="_blank"
          rel="noopener noreferrer" data-track="csrep-profile">
          [view on csrep →]
        </a>
        {data.faceitUrl && (
          <a className="pp-link" href={data.faceitUrl} target="_blank"
            rel="noopener noreferrer" data-track="faceit-profile">
            [faceit profile →]
          </a>
        )}
      </span>
      {/* Official badge from CSRep's own extension (light text, for dark
          backgrounds), unmodified. */}
      <a className="pp-leetify-badge" href={data.profileUrl} target="_blank"
        rel="noopener noreferrer" aria-label="Data provided by CSRep">
        <img src="/csrep/csrep-badge.png" alt="Data provided by CSRep" />
      </a>
    </div>
  );
}

// Same one-liner as LeetifyPlaceholder. CSRep answers unknown players either
// with a 404 or with a stub profile where every field is empty; both land here
// so the card never renders as two bare headings.
export function CsrepPlaceholder({ steamId, reason }) {
  const message = reason === 'upstream_404' || reason === 'no_data'
    ? 'no csrep data for this player'
    : 'csrep stats unavailable right now';
  return (
    <section className="pp-card pp-leetify-empty">
      <span className="pp-comment">{`// ${message}`}</span>
      <a className="pp-link pp-leetify-link" href={`https://csrep.gg/player/${steamId}`}
        target="_blank" rel="noopener noreferrer" data-track="csrep-profile">
        [view on csrep →]
      </a>
    </section>
  );
}

// premierColor: App.js's premierTierColor, so both cards share one tier scale.
export default function CsrepCard({ steamId, data, premierColor }) {
  if (data.restricted) {
    return (
      <section className="pp-card">
        <div className="pp-comment">{'// csrep profile is restricted'}</div>
        <Footer data={data} />
      </section>
    );
  }

  const breakdown = [
    data.statistical != null && { label: 'statistical', value: pct(data.statistical) },
    data.accountFlags != null && { label: 'accountFlags', value: pct(data.accountFlags) },
    data.anomalies != null && { label: 'anomalies', value: pct(data.anomalies) },
  ].filter(Boolean);

  const ranks = [
    data.faceit && data.faceit.current > 0 && {
      label: 'faceitElo', value: count(data.faceit.current), color: '#fb923c',
      peak: data.faceit.peak != null ? count(data.faceit.peak) : null,
    },
    ...data.premier.map((s) => ({
      label: `premierS${s.season}`,
      value: s.current != null ? count(s.current) : '-',
      color: premierColor(s.current),
      peak: s.peak != null ? count(s.peak) : null,
      peakColor: premierColor(s.peak),
    })),
  ].filter(Boolean);

  const c = data.commendations;

  if (data.trustScore == null && breakdown.length === 0 && !data.accountBonus && !c
    && ranks.length === 0 && data.bans.length === 0 && !data.autoflaggedAt) {
    return <CsrepPlaceholder steamId={steamId} reason="no_data" />;
  }

  return (
    <section className="pp-card pp-csrep">
      <div className="pp-leetify-grid">
        <div>
          <div className="pp-comment">{'// trust'}</div>
          {data.trustScore != null && (
            <div className="pp-csrep-score">
              <span className="pp-label">
                trustScore <span className="pp-csrep-tag" title={DISCLAIMER}>probabilistic</span>
              </span>
              <span className="pp-csrep-score-value">{Math.round(data.trustScore)}</span>
            </div>
          )}
          {breakdown.map((s) => (
            <div className="pp-skill" key={s.label}>
              <div className="pp-skill-head">
                <span>{s.label}</span>
                <span className="pp-skill-value">{s.value}</span>
              </div>
              <div className="pp-bar">
                <span className="pp-bar-fill" style={{ width: `${Math.max(0, Math.min(100, s.value))}%` }}></span>
              </div>
            </div>
          ))}
          {data.accountBonus != null && data.accountBonus !== 0 && (
            <div className="pp-skill pp-skill-head">
              <span>accountBonus</span>
              <span className="pp-skill-value">{`${data.accountBonus > 0 ? '+' : ''}${pct(data.accountBonus)}`}</span>
            </div>
          )}
          {c && (
            <div className="pp-skill pp-skill-head pp-commend-row">
              <span>commendations</span>
              <span className="pp-commends">
                {COMMENDATIONS.map(([k, Icon]) => c[k] != null && (
                  <span className="pp-commend" key={k} title={k}>
                    <Icon />
                    {count(c[k])}
                  </span>
                ))}
              </span>
            </div>
          )}
        </div>
        <div>
          <div className="pp-comment">{'// ranks'}</div>
          <div className="pp-tiles">
            {ranks.map((r) => (
              <div className="pp-tile" key={r.label}>
                <div className="pp-label">{r.label}</div>
                <div className="pp-tile-value" style={r.color ? { color: r.color } : undefined}>{r.value}</div>
                {r.peak && (
                  <div className="pp-csrep-peak">
                    peak <span style={r.peakColor || r.color ? { color: r.peakColor || r.color } : undefined}>{r.peak}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {(data.bans.length > 0 || data.autoflaggedAt) && (
        <dl className="pp-kv pp-csrep-flags">
          {data.autoflaggedAt && (
            <div className="pp-kv-row">
              <dt>autoflagged</dt>
              <dd>{formatDate(data.autoflaggedAt)}</dd>
            </div>
          )}
          {data.bans.map((b, i) => (
            <div className="pp-kv-row" key={i}>
              <dt title={b.reason || undefined}>{`${b.source.toLowerCase()}Ban`}</dt>
              <dd>
                {`"${b.type.toLowerCase()}"`}
                {b.startsAt && ` · ${formatDate(b.startsAt)}`}
                {b.endsAt && ` → ${formatDate(b.endsAt)}`}
              </dd>
            </div>
          ))}
        </dl>
      )}

      <Footer data={data} />
    </section>
  );
}
